import { Component, computed, input, InputSignal, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { ButtonColor, ButtonVariant } from '../../types/interfaces/ui/button/button';

@Component({
  selector: 'bi-button',
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './button.html',
  styleUrl: './button.scss',
})
export class Button {
  /** Visual variant: text (no fill/outline), filled, or outlined. Matches Material button appearance. */
  readonly variant = input<ButtonVariant>('text');

  /** Color variant: primary (brand blue), secondary (amber), or basic (text color). */
  readonly color = input<ButtonColor>('primary');

  readonly resolvedColor = computed(() => {
    switch (this.color()) {
      case 'secondary':
        return 'var(--color-secondary)';
      case 'basic':
        return 'var(--color-text)';
      default:
        return 'var(--color-primary)';
    }
  });

  readonly resolvedBorderColor = computed(() => {
    if (this.variant() !== 'outlined') return 'transparent';
    if (this.color() === 'basic') return 'var(--color-border-default)';
    return this.resolvedColor();
  });

  /** When true, the button is disabled. */
  readonly disabled = input<boolean>(false);

  /** Optional Material icon name (e.g. 'add', 'edit') shown to the left of the label. */
  readonly icon = input<string | undefined>(undefined);

  readonly customIconPath = input<string | undefined>(undefined);

  /** Height of the button in pixels (default: 48). */
  readonly height = input<number>(48);

  /** Font size of the button label text in pixels (default: 16). */
  readonly fontSizeText = input<number>(16);

  /** Font size of the button icon in pixels (default: 24). */
  readonly fontSizeIcon = input<number>(24);

  readonly lineHeight = computed(() => this.height());

  readonly iconSize = computed(() => this.fontSizeIcon());

  /** Emits when the button is clicked. */
  readonly clicked = output<MouseEvent>();
}
